import type { ApiColumn, PaginationMeta } from "@/types/api";
import type { AllLocationsResponseScreener } from "@/types/screeners";
import type { UserListUser } from "@/types/user";
import type { Nullable } from "@/types/utils";

// Life would be so much better if TS just let me extend enums.
// This is broken into two enums - Status and MetaStatus. This is done to ease
// typing, because an individual location cannot have a "Mixed" status. "Mixed"
// only emerges when looking at an aggregate of screener locations
export const IndividualScreenerLocationStatus = {
  Live: "Live",
  Paused: "Paused",
} as const;

const ScreenerLocationMetaStatus = {
  Mixed: "Mixed",
} as const;

export const ScreenerLocationStatus = {
  ...IndividualScreenerLocationStatus,
  ...ScreenerLocationMetaStatus,
} as const;

export type ScreenerLocationStatusType =
  (typeof ScreenerLocationStatus)[keyof typeof ScreenerLocationStatus];

export const UNPUBLISHED = "Not published" as const;

export type ScreenerLocationStatusTypeWithUnpublished =
  | ScreenerLocationStatusType
  | typeof UNPUBLISHED;

export type IndividualScreenerLocationStatusType =
  (typeof IndividualScreenerLocationStatus)[keyof typeof IndividualScreenerLocationStatus];

// Extracted from ScreenerLocation as there are a great number of utilities that
// deal with wages from ScreenerLocations and ScreenerLocationDrafts
export interface Wages {
  maxPayHourly?: number;
  minPayHourly?: number;
}

export enum WageType {
  DontInclude = "Don't include",
  SingleWage = "Single wage",
  WageRange = "Wage range",
}

// `max/minPayHourly` will only appear on the manage locations response.
// Locations table data makes use of the pre-formatted `wage` property
export interface ScreenerLocation extends Wages {
  assignedAt: Nullable<string>;
  assignedTo: Nullable<UserListUser>;
  atsJobId: Nullable<string>;
  createdAt: string;
  id: string;
  internalName?: string;
  jobName: string;
  lastSubmissionDate: Nullable<string>;
  locationName: string;
  positionLink: string;
  sessionCount?: number;
  shareLink: Nullable<string>;
  shareLinkSlug: Nullable<string>;
  status: IndividualScreenerLocationStatusType;
  unviewedSessionCount: number;
  wage?: string;
}

export interface ScreenerLocationDraft extends Wages {
  atsJobId: Nullable<string>;
  id: string;
  // This property only exists on the draft. Used to indicate whether
  // this is a brand new location or a location being edited.
  isNew: boolean;
  locationName: string;
  sessionCount?: number;
  shareLinkSlug: Nullable<string>;
  // This will always be "Live" for new screeners
  status: IndividualScreenerLocationStatusType;
  // Another draft-only property. Initially inferred from the existence of min
  // and/or max pay
  wageType: WageType;
}

export const propertiesToCleanFromLocationDraft = [
  "id",
  "isNew",
  "sessionCount",
  "wageType",
] as const;
export interface CleanLocationDraftForServer
  extends Omit<
    ScreenerLocationDraft,
    | (typeof propertiesToCleanFromLocationDraft)[number]
    // We exclude and then re-add min/max pay here to transform their type from
    // Maybe<number> to Nullable<number> as JSON does not support undefined
    | "maxPayHourly"
    | "minPayHourly"
  > {
  maxPayHourly: Nullable<number>;
  minPayHourly: Nullable<number>;
}

export interface LocationsAvailableFilters {
  assignedToUsers: UserListUser[];
  locationNames: string[];
  statuses: IndividualScreenerLocationStatusType[];
}

export interface AssignedLocationsAvailableFilters
  extends Omit<LocationsAvailableFilters, "assignedToUsers"> {
  jobNames: string[];
}

export interface ScreenerLocationsTableResponse {
  availableFilters: AssignedLocationsAvailableFilters;
  columns: ApiColumn<ScreenerLocation>[];
  locations: ScreenerLocation[];
  pagination: PaginationMeta;
}

export interface AllLocationsResponse {
  availableFilters: LocationsAvailableFilters;
  columns: ApiColumn<ScreenerLocation>[];
  pagination: PaginationMeta;
  screener: AllLocationsResponseScreener;
  locations: ScreenerLocation[];
}

export interface PromptData {
  screenerLocationPromptId: string;
  synthesizedPromptText: string;
}
